import { supabase } from '../config/supabase';
import { logger } from '../utils/logger';
import i18n from '../i18n/config.js';
import { runQuery } from './api';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';

/**
 * Get current user's JWT token for API authentication
 * @returns {Promise<string|null>} JWT token or null if not authenticated
 */
const getAuthToken = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error('Error getting session:', error);
      return null;
    }
    
    if (!session) {
      logger.warn('No active session found');
      return null;
    }
    
    // Check if the session is expired
    if (session.expires_at && new Date(session.expires_at * 1000) <= new Date()) {
      logger.warn('Session expired, attempting to refresh...');
      
      // Try to refresh the session
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError || !refreshData.session) {
        logger.error('Failed to refresh session:', refreshError);
        return null;
      }
      
      return refreshData.session.access_token;
    }
    
    return session.access_token;
  } catch (error) {
    logger.error('Error in getAuthToken:', error);
    return null;
  }
};

/**
 * Get all dashboards for a company
 * @param {number} companyId - ID of the company
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Array>} Array of dashboard objects
 */
export const getDashboards = async (companyId, signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = { method: 'GET', headers };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${API_BASE}/dashboards`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Failed to get dashboards:', error);
    throw error;
  }
};

/**
 * Get a specific dashboard with all its widgets
 * @param {number} dashboardId - ID of the dashboard
 * @param {number} companyId - ID of the company
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} Dashboard object with widgets
 */
export const getDashboardWithWidgets = async (dashboardId, companyId, signal = null) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const fetchOptions = { method: 'GET', headers };
    if (signal) fetchOptions.signal = signal;

    const response = await fetch(`${API_BASE}/dashboards/${dashboardId}`, fetchOptions);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error('Failed to get dashboard with widgets:', error);
    throw error;
  }
};

/**
 * Create a new dashboard
 * @param {string} name - Dashboard name
 * @param {string} structure - Grid structure (e.g., "2x2")
 * @param {number} maxWidgets - Maximum number of widgets
 * @param {number} companyId - ID of the company
 * @returns {Promise<Object>} Created dashboard object
 */
export const createDashboard = async (name, structure, maxWidgets, companyId) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/dashboards`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        name,
        structure,
        max_widgets: maxWidgets
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create dashboard');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    logger.info('Dashboard created:', data.id);
    return data;
  } catch (error) {
    logger.error('Failed to create dashboard:', error);
    throw error;
  }
};

/**
 * Add a widget to a dashboard
 * @param {number} dashboardId - ID of the dashboard
 * @param {number} queryId - ID of the query to link
 * @param {string} name - Widget display name
 * @param {string} defaultChart - Default chart type
 * @param {number} companyId - ID of the company
 * @returns {Promise<Object>} Created widget object
 */
export const addWidgetToDashboard = async (dashboardId, queryId, name, defaultChart, companyId) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/dashboards/${dashboardId}/widgets`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query_id: queryId,
        name,
        default_chart: defaultChart
      })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        const errorData = await response.json();
        throw new Error(errorData.detail || i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 404) {
        throw new Error('Dashboard not found');
      }
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to add widget');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    logger.info('Widget added to dashboard:', data.id);
    return data;
  } catch (error) {
    logger.error('Failed to add widget to dashboard:', error);
    throw error;
  }
};

/**
 * Update widget properties
 * @param {number} widgetId - ID of the widget
 * @param {Object} updates - Properties to update
 * @param {string} updates.name - New widget name (optional)
 * @param {string} updates.default_chart - New default chart type (optional)
 * @param {number} updates.row - New row position (optional)
 * @param {number} updates.column - New column position (optional)
 * @param {number} companyId - ID of the company
 * @returns {Promise<Object>} Updated widget object
 */
export const updateWidget = async (widgetId, updates, companyId) => {
  try {
    const token = await getAuthToken();
    if (!token) {
      throw new Error(i18n.t('errors.noAuthToken'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/widgets/${widgetId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 404) {
        throw new Error('Widget not found');
      }
      if (response.status === 400 || response.status === 409) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update widget');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    logger.info('Widget updated:', widgetId);
    return data;
  } catch (error) {
    logger.error('Failed to update widget:', error);
    throw error;
  }
};

/**
 * Refresh widget data by re-running its query
 * @param {number} queryId - ID of the query to run
 * @param {number} companyId - ID of the company
 * @param {AbortSignal} [signal] - Optional abort signal (e.g. on company switch)
 * @returns {Promise<Object>} Query result data
 */
export const refreshWidgetData = async (queryId, companyId, signal = null) => {
  try {
    const result = await runQuery(queryId, companyId, signal);
    return result;
  } catch (error) {
    logger.error('Failed to refresh widget data:', error);
    throw error;
  }
};

/**
 * Delete a widget from a dashboard
 * @param {number} widgetId - ID of the widget to delete
 * @param {number} companyId - ID of the company
 * @returns {Promise<void>}
 */
export const deleteWidget = async (widgetId, companyId) => {
  try {
    const token = await getAuthToken();
    
    if (!token) {
      throw new Error(i18n.t('errors.authFailed'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/widgets/${widgetId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 404) {
        throw new Error('Widget not found');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    logger.info('Widget deleted:', widgetId);
    // 204 No Content - no response body
  } catch (error) {
    logger.error('Failed to delete widget:', error);
    throw error;
  }
};

/**
 * Update positions of multiple widgets (for drag-and-drop)
 * @param {number} dashboardId - ID of the dashboard
 * @param {Array<{widget_id: number, row: number, column: number}>} updates - Array of position updates
 * @param {number} companyId - ID of the company
 * @returns {Promise<Array>} Updated widgets
 */
export const updateWidgetPositions = async (dashboardId, updates, companyId) => {
  try {
    const token = await getAuthToken();
    
    if (!token) {
      throw new Error(i18n.t('errors.authFailed'));
    }

    if (!companyId) {
      throw new Error(i18n.t('errors.companyIdRequired'));
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      'Company-Id': companyId.toString()
    };

    const response = await fetch(`${API_BASE}/dashboards/${dashboardId}/widgets/positions`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ updates })
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error(i18n.t('errors.authFailed'));
      }
      if (response.status === 403) {
        throw new Error(i18n.t('errors.noCompanyDataAccess'));
      }
      if (response.status === 404) {
        throw new Error('Dashboard or widgets not found');
      }
      if (response.status === 400) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update widget positions');
      }
      throw new Error(i18n.t('errors.httpError', { status: response.status }));
    }

    const data = await response.json();
    logger.info('Widget positions updated:', updates.length);
    return data;
  } catch (error) {
    logger.error('Failed to update widget positions:', error);
    throw error;
  }
};

